import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { ClipboardList, ExternalLink } from "lucide-react";
import type { MessagingRule } from "@shared/schema";

interface ActivityRow {
  source: string;
  logId: string;
  memberId: number;
  memberName: string;
  sentAt: string;
  messagePreview: string | null;
  memberChatId: number | null;
}

interface ActivityResponse {
  rows: ActivityRow[];
  total: number;
  limit: number;
  offset: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  rule: MessagingRule | null;
}

function fmtDateTime(iso: string) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function RuleActivityDrawer({ open, onClose, rule }: Props) {
  const [, setLocation] = useLocation();

  const { data, isLoading } = useQuery<ActivityResponse>({
    queryKey: ["/api/app/messages/rules", rule?.id, "activity"],
    queryFn: async () => {
      const res = await fetch(`/api/app/messages/rules/${rule!.id}/activity?limit=100`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    },
    enabled: open && rule != null,
    staleTime: 30_000,
  });

  function navigateToThread(row: ActivityRow) {
    const params = new URLSearchParams();
    params.set("member", String(row.memberId));
    if (row.memberChatId != null) {
      params.set("chat", String(row.memberChatId));
    }
    onClose();
    setLocation(`/app/messages?${params.toString()}`);
  }

  const rows = data?.rows ?? [];

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg overflow-y-auto"
        data-testid="drawer-rule-activity"
      >
        <SheetHeader className="mb-4">
          <SheetTitle className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4" />
            Activity Log
          </SheetTitle>
          {rule && (
            <p className="text-sm text-gray-500 mt-0.5 truncate" data-testid="text-activity-rule-name">
              {rule.name}
            </p>
          )}
        </SheetHeader>

        {isLoading ? (
          <div className="space-y-3" data-testid="skeleton-activity-rows">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center gap-3 py-16 text-center"
            data-testid="empty-rule-activity"
          >
            <ClipboardList className="h-8 w-8 text-gray-300" />
            <p className="text-sm text-gray-500 font-medium">No activity yet</p>
            <p className="text-xs text-gray-400 max-w-[240px]">
              When this rule fires and sends a message, each send will appear here.
            </p>
          </div>
        ) : (
          <>
            <p className="text-xs text-gray-400 mb-3" data-testid="text-activity-total">
              {data!.total} send{data!.total === 1 ? "" : "s"} total
            </p>
            <div className="space-y-1" data-testid="list-activity-rows">
              {rows.map((row) => (
                <button
                  key={row.logId + row.source}
                  type="button"
                  onClick={() => navigateToThread(row)}
                  className="w-full text-left rounded-lg border border-gray-100 bg-gray-50 hover:bg-blue-50 hover:border-blue-100 transition-colors p-3 group"
                  data-testid={`row-activity-${row.source}-${row.logId}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span
                          className="text-sm font-medium text-gray-900 truncate"
                          data-testid={`text-activity-member-${row.logId}`}
                        >
                          {row.memberName}
                        </span>
                      </div>
                      {row.messagePreview && (
                        <p
                          className="mt-0.5 text-xs text-gray-500 line-clamp-2"
                          data-testid={`text-activity-preview-${row.logId}`}
                        >
                          {row.messagePreview}
                        </p>
                      )}
                      <p className="mt-1 text-[11px] text-gray-400" data-testid={`text-activity-time-${row.logId}`}>
                        {fmtDateTime(row.sentAt)}
                      </p>
                    </div>
                    <ExternalLink className="h-3.5 w-3.5 text-gray-300 group-hover:text-blue-500 flex-shrink-0 mt-0.5" />
                  </div>
                </button>
              ))}
            </div>
            {data!.total > rows.length && (
              <p className="mt-3 text-xs text-gray-400 text-center" data-testid="text-activity-truncation-note">
                Showing {rows.length} of {data!.total} sends. Contact support for a full export.
              </p>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
